import {
  LLM_PROVIDERS,
  type LlmProvider,
  LlmProviderSchema,
  MODEL_TIERS,
  type ModelTier,
  type ModelTierBinding,
} from '@sbc/domain-models/agents';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAvailableModels, useModelTiers, useUpdateModelTier } from '@/lib/api/model-tiers';

const TIER_HINTS: Record<ModelTier, string> = {
  large: 'Largest and most capable. Slowest and most expensive.',
  medium: 'The everyday driver for customer-facing agents.',
  small: 'Cheapest. Also drives the eval judge and the feedback classifier.',
  'claude-large': 'Most capable Claude on Vertex AI, for the hardest customer replies.',
  'claude-medium': 'Everyday Claude on Vertex AI. A candidate for the Intercom drafter.',
  'claude-small': 'Smallest Claude on Vertex AI, for short, high-volume jobs.',
  eval: 'For trying a new model in an eval run. No live agent should use it.',
  image:
    'Generates images. Only the product-registration thumbnail tool uses it, and no agent can run on it.',
};

const PROVIDER_LABELS: Record<LlmProvider, string> = {
  gemini: 'Google Gemini',
  openai: 'OpenAI',
  anthropic: 'Anthropic Claude (Vertex AI)',
};

export function ModelsPage() {
  const tiersQuery = useModelTiers();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Models</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Which provider and model each tier points at. Agents pick one of the three size tiers, so
          repointing one here moves every agent on it — including onto a different provider.
          Changing <strong>small</strong> also moves the eval judge, which means eval scores before
          and after are not comparable. <strong>image</strong> is a capability tier rather than a
          size: it must point at an image-generation model, and only the product-registration
          thumbnail tool reads it.
        </p>
      </div>

      {tiersQuery.isPending && <p className="text-sm">Loading…</p>}
      {tiersQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load tiers: {tiersQuery.error.message}
        </p>
      )}

      {tiersQuery.data && (
        <div className="space-y-4">
          {MODEL_TIERS.map((tier) => {
            const binding = tiersQuery.data.find((row) => row.tier === tier);
            return binding ? <TierRow key={tier} binding={binding} /> : null;
          })}
        </div>
      )}
    </div>
  );
}

// One row per tier, and its own component so each can hold the provider it is being edited
// against — the model list a row offers depends on that provider, not on the saved one.
function TierRow({ binding }: { binding: ModelTierBinding }) {
  const [provider, setProvider] = useState<LlmProvider>(binding.provider);
  const [modelId, setModelId] = useState<string>(binding.modelId);
  const modelsQuery = useAvailableModels(provider);
  const updateTier = useUpdateModelTier();

  const models = modelsQuery.data ?? [];
  const unchanged = provider === binding.provider && modelId === binding.modelId;

  return (
    <div className="space-y-2 rounded-md border p-4">
      <Label htmlFor={`tier-${binding.tier}`} className="capitalize">
        {binding.tier}
      </Label>
      <p className="text-xs text-[var(--color-muted-foreground)]">{TIER_HINTS[binding.tier]}</p>
      <div className="flex items-center gap-2">
        <Select
          value={provider}
          onValueChange={(value) => {
            setProvider(LlmProviderSchema.parse(value));
            // The old id almost never exists on the new provider, so clearing it forces a
            // deliberate pick rather than a save that the API would reject.
            setModelId('');
          }}
        >
          <SelectTrigger id={`provider-${binding.tier}`} className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LLM_PROVIDERS.map((name) => (
              <SelectItem key={name} value={name}>
                {PROVIDER_LABELS[name]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={modelId} onValueChange={setModelId}>
          <SelectTrigger id={`tier-${binding.tier}`} className="max-w-md flex-1">
            <SelectValue placeholder="Select a model" />
          </SelectTrigger>
          <SelectContent>
            {models.map((model) => (
              <SelectItem key={model.id} value={model.id}>
                {model.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          disabled={unchanged || !modelId || updateTier.isPending}
          onClick={() => updateTier.mutate({ tier: binding.tier, provider, modelId })}
        >
          Save
        </Button>
      </div>
      {modelsQuery.isError && (
        <p className="text-xs text-[var(--color-destructive)]">
          Failed to load the {PROVIDER_LABELS[provider]} model list: {modelsQuery.error.message}
        </p>
      )}
      {updateTier.isError && (
        <p className="text-xs text-[var(--color-destructive)]">
          Save failed: {updateTier.error.message}
        </p>
      )}
      {!modelsQuery.isPending &&
        provider === binding.provider &&
        !models.some((model) => model.id === binding.modelId) && (
          <p className="text-xs text-[var(--color-destructive)]">
            Currently set to <code>{binding.modelId}</code>, which the configured{' '}
            {PROVIDER_LABELS[provider]} key does not serve. Agents on this tier will fail until it
            is repointed.
          </p>
        )}
    </div>
  );
}
